import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowUpDown,
  Check,
  Filter,
  Search,
  Truck,
  X,
} from 'lucide-react';
import { useUserProfile } from '../context/UserProfileContext';
import { useJobs, Job, JobSortField, SortDir } from '../hooks/useJobs';
import { CrewAssignmentModal } from '../components/jobs/CrewAssignmentModal';
import { TruckAssignmentModal } from '../components/jobs/TruckAssignmentModal';
import { MoveCard } from '../components/ui/MoveCard';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { api } from '../lib/api';
import {
  JobStatus,
  JOB_STATUS_CLASSES,
  JOB_STATUS_LABEL,
} from '../types/admin';

// ── Sort options ──────────────────────────────────────────────────────────────

interface SortOption {
  field: JobSortField;
  dir: SortDir;
  label: string;
}

const SORT_OPTIONS: SortOption[] = [
  { field: 'movingDate', dir: 'asc', label: 'Move date (soonest)' },
  { field: 'movingDate', dir: 'desc', label: 'Move date (latest)' },
  { field: 'customerCompanyName', dir: 'asc', label: 'Company A–Z' },
  { field: 'customerCompanyName', dir: 'desc', label: 'Company Z–A' },
  { field: 'jobStatus', dir: 'asc', label: 'Status A–Z' },
];

const ALL_JOB_STATUSES: JobStatus[] = ['scheduled', 'in_progress', 'completed', 'on_hold', 'cancelled'];

/** Statuses shown as counters at the top */
const COUNTER_STATUSES: JobStatus[] = ['scheduled', 'in_progress', 'on_hold'];

/** Default visible statuses — active jobs only */
const DEFAULT_VISIBLE: Set<JobStatus> = new Set(['scheduled', 'in_progress', 'on_hold']);

// ── Sort Sheet ──────────────────────────────────────────────────────────────

function SortSheet({
  current,
  onSelect,
  onClose,
}: {
  current: SortOption;
  onSelect: (o: SortOption) => void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-10 md:rounded-2xl md:max-w-sm md:w-full md:pb-6">
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-h3 text-midnight">Sort Jobs</h2>
          <button type="button" onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate hover:text-midnight hover:bg-mist transition-colors"
            aria-label="Close"><X size={16} /></button>
        </div>
        <ul className="flex flex-col gap-1.5">
          {SORT_OPTIONS.map(o => {
            const active = o.field === current.field && o.dir === current.dir;
            return (
              <li key={`${o.field}-${o.dir}`}>
                <button type="button" onClick={() => onSelect(o)}
                  className={`w-full flex items-center gap-3 h-11 px-4 rounded-xl text-[13px] font-medium transition-colors
                    ${active ? 'bg-plum/10 text-plum' : 'text-midnight hover:bg-mist'}`}>
                  {o.label}
                  {active && <Check size={15} className="ml-auto text-plum" />}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

// ── Filter Sheet ────────────────────────────────────────────────────────────

interface JobFilterState {
  visibleStatuses: Set<JobStatus>;
  myJobsOnly: boolean;
  typeCommercial: boolean;
  typeResidential: boolean;
}

function FilterSheet({
  filters,
  onApply,
  onClose,
  showTypeFilter,
}: {
  filters: JobFilterState;
  onApply: (f: JobFilterState) => void;
  onClose: () => void;
  showTypeFilter: boolean;
}) {
  const [visibleStatuses, setVisibleStatuses] = useState<Set<JobStatus>>(new Set(filters.visibleStatuses));
  const [myJobsOnly, setMyJobsOnly] = useState(filters.myJobsOnly);
  const [typeCommercial, setTypeCommercial] = useState(filters.typeCommercial);
  const [typeResidential, setTypeResidential] = useState(filters.typeResidential);

  const allSelected = visibleStatuses.size === ALL_JOB_STATUSES.length;

  function toggleStatus(s: JobStatus) {
    setVisibleStatuses(prev => {
      const next = new Set(prev);
      if (next.has(s)) {
        if (next.size === 1) return new Set(ALL_JOB_STATUSES);
        next.delete(s);
      } else {
        next.add(s);
      }
      return next;
    });
  }

  function selectAll() {
    setVisibleStatuses(new Set(ALL_JOB_STATUSES));
  }

  function handleApply() {
    onApply({ visibleStatuses, myJobsOnly, typeCommercial, typeResidential });
    onClose();
  }

  function handleReset() {
    onApply({
      visibleStatuses: new Set(DEFAULT_VISIBLE),
      myJobsOnly: false,
      typeCommercial: true,
      typeResidential: true,
    });
    onClose();
  }

  const CheckboxIcon = ({ checked }: { checked: boolean }) => (
    <span className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0
      ${checked ? 'border-plum bg-plum' : 'border-stone'}`}>
      {checked && (
        <svg viewBox="0 0 10 8" fill="none" className="w-2.5 h-2 text-white">
          <path d="M1 4l3 3 5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  );

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-10 max-h-[80vh] overflow-y-auto
                      md:rounded-2xl md:max-w-sm md:w-full md:pb-6">
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-h3 text-midnight">Filter Jobs</h2>
          <button type="button" onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate hover:text-midnight hover:bg-mist transition-colors"
            aria-label="Close"><X size={16} /></button>
        </div>

        {/* Type filter — at the very top */}
        {showTypeFilter && (
          <>
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">Type</p>
            <div className="flex flex-col gap-2 mb-5">
              <button type="button" onClick={() => setTypeCommercial(v => !v)}
                className={`w-full flex items-center gap-3 h-11 px-4 rounded-xl border text-left transition-all duration-150
                  ${typeCommercial ? 'border-plum bg-plum/5' : 'border-stone bg-white'}`}>
                <CheckboxIcon checked={typeCommercial} />
                <span className={`text-small font-medium ${typeCommercial ? 'text-plum' : 'text-midnight'}`}>Commercial</span>
              </button>
              <button type="button" onClick={() => setTypeResidential(v => !v)}
                className={`w-full flex items-center gap-3 h-11 px-4 rounded-xl border text-left transition-all duration-150
                  ${typeResidential ? 'border-plum bg-plum/5' : 'border-stone bg-white'}`}>
                <CheckboxIcon checked={typeResidential} />
                <span className={`text-small font-medium ${typeResidential ? 'text-plum' : 'text-midnight'}`}>Residential</span>
              </button>
            </div>
          </>
        )}

        {/* Status filters */}
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">Status</p>
        <div className="flex flex-col gap-1.5 mb-5">
          <button type="button" onClick={selectAll}
            className={`w-full flex items-center gap-3 h-11 px-4 rounded-xl text-[13px] font-medium transition-colors
              ${allSelected ? 'bg-plum/10 text-plum' : 'text-midnight hover:bg-mist'}`}>
            All statuses
            {allSelected && <Check size={15} className="ml-auto text-plum" />}
          </button>
          {ALL_JOB_STATUSES.map(s => {
            const active = visibleStatuses.has(s);
            const classes = JOB_STATUS_CLASSES[s];
            return (
              <button key={s} type="button" onClick={() => toggleStatus(s)}
                className={`w-full flex items-center gap-3 h-11 px-4 rounded-xl text-[13px] font-medium transition-colors
                  ${active ? 'bg-plum/10 text-plum' : 'text-midnight hover:bg-mist'}`}>
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${classes}`}>
                  {JOB_STATUS_LABEL[s]}
                </span>
                {active && <Check size={15} className="ml-auto text-plum" />}
              </button>
            );
          })}
        </div>

        {/* Scope */}
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">Scope</p>
        <button type="button" onClick={() => setMyJobsOnly(v => !v)}
          className={`w-full flex items-center gap-3 h-11 px-4 rounded-xl border text-left mb-5 transition-all duration-150
            ${myJobsOnly ? 'border-plum bg-plum/5' : 'border-stone bg-white'}`}>
          <CheckboxIcon checked={myJobsOnly} />
          <div>
            <p className={`text-small font-medium ${myJobsOnly ? 'text-plum' : 'text-midnight'}`}>My Jobs only</p>
            <p className="text-[11px] text-slate">Show only jobs assigned to you</p>
          </div>
        </button>

        <div className="flex gap-3">
          <button type="button" onClick={handleReset}
            className="flex-1 h-12 rounded-xl border border-stone text-small font-semibold text-slate
                       hover:border-plum/40 hover:text-midnight active:bg-mist transition-colors duration-150">
            Reset
          </button>
          <button type="button" onClick={handleApply}
            className="flex-1 h-12 rounded-xl bg-plum text-white text-[15px] font-semibold
                       hover:bg-rose active:opacity-80 transition-colors duration-150">
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function MoverJobsPage({ embedded, onSidebarContent }: { embedded?: boolean; onSidebarContent?: (content: React.ReactNode) => void } = {}) {
  const navigate = useNavigate();
  const { activeOrg, hasActiveSubscription, currentUser } = useUserProfile();
  const orgId = activeOrg?.orgId ?? null;
  const isMover = activeOrg?.role === 'mover';
  const noSubscription = !hasActiveSubscription && !currentUser?.isPlatformAdmin;
  const uid = currentUser?.uid ?? undefined;

  // Feature flag checks
  const commercialEnabled = activeOrg?.featureCommercialMovingQuotes !== false;
  const residentialEnabled = activeOrg?.featureResidentialMovingQuotes ?? false;
  const bothTypesEnabled = commercialEnabled && residentialEnabled;

  if (activeOrg?.featureJobs === false) {
    return (
      <div className="px-4 pt-16 flex flex-col items-center text-center gap-3">
        <p className="text-small text-slate font-semibold">Jobs is not enabled for this organization.</p>
        <button type="button" onClick={() => navigate('/')} className="text-small text-plum hover:underline">Go to Quotes</button>
      </div>
    );
  }

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState<JobSortField>('movingDate');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [jobFilters, setJobFilters] = useState<JobFilterState>({
    visibleStatuses: new Set(DEFAULT_VISIBLE),
    myJobsOnly: false,
    typeCommercial: true,
    typeResidential: true,
  });
  const [showSort, setShowSort] = useState(false);
  const [showFilter, setShowFilter] = useState(false);


  // Debounce search
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    timerRef.current = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timerRef.current);
  }, [search]);

  // "My Jobs" filter: pass current user's UID to API
  const myJobsUid = jobFilters.myJobsOnly ? uid : undefined;

  // Fetch ALL jobs (no status filter to API — we filter client-side so we get stats for all statuses)
  const { jobs, loading, loadingMore, error, hasMore, stats, fetchMore, refetch } = useJobs(orgId, {
    search: debouncedSearch || undefined,
    sortBy,
    sortDir,
    moveLeadUserId: myJobsUid ?? undefined,
  });

  // Crew / Truck assignment
  const [crewAssignJob, setCrewAssignJob] = useState<Job | null>(null);
  const [truckAssignJob, setTruckAssignJob] = useState<Job | null>(null);

  const handleCrewSave = useCallback(async (crew: import('../hooks/useJobs').AssignedCrewMember[]) => {
    if (!crewAssignJob || !orgId) return;
    await api.patch(`/jobs/${orgId}/${crewAssignJob.id}`, { assignedCrew: crew });
    setCrewAssignJob(null);
    refetch();
  }, [crewAssignJob, orgId, refetch]);

  const handleTruckSave = useCallback(async (trucks: import('../hooks/useJobs').AssignedTruck[]) => {
    if (!truckAssignJob || !orgId) return;
    await api.patch(`/jobs/${orgId}/${truckAssignJob.id}`, { assignedTrucks: trucks });
    setTruckAssignJob(null);
    refetch();
  }, [truckAssignJob, orgId, refetch]);

  // Client-side status + type filter
  const filteredJobs = useMemo(() => {
    return jobs.filter(j => {
      if (!jobFilters.visibleStatuses.has(j.jobStatus)) return false;
      if (bothTypesEnabled) {
        const isCommercial = j.quoteType !== 'residential';
        if (isCommercial && !jobFilters.typeCommercial) return false;
        if (!isCommercial && !jobFilters.typeResidential) return false;
      }
      return true;
    });
  }, [jobs, jobFilters.visibleStatuses, jobFilters.typeCommercial, jobFilters.typeResidential, bothTypesEnabled]);

  const hasActiveFilter = jobFilters.visibleStatuses.size < ALL_JOB_STATUSES.length
    || jobFilters.myJobsOnly
    || (bothTypesEnabled && (!jobFilters.typeCommercial || !jobFilters.typeResidential));

  const currentSort = useMemo(() =>
    SORT_OPTIONS.find(o => o.field === sortBy && o.dir === sortDir) ?? SORT_OPTIONS[0],
    [sortBy, sortDir],
  );

  // ── Infinite scroll ──
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      entries => { if (entries[0].isIntersecting) fetchMore(); },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [fetchMore]);

  // ── Handlers ──
  function handleSortSelect(o: SortOption) {
    setSortBy(o.field);
    setSortDir(o.dir);
    setShowSort(false);
  }

  /** Tap a stat card to isolate that status (or toggle off to restore defaults) */
  const handleStatTap = useCallback((s: JobStatus) => {
    setJobFilters(prev => {
      const { visibleStatuses } = prev;
      if (visibleStatuses.size === 1 && visibleStatuses.has(s)) return { ...prev, visibleStatuses: new Set(DEFAULT_VISIBLE) };
      return { ...prev, visibleStatuses: new Set([s]) };
    });
  }, []);


  // Active filter count for badge
  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (jobFilters.visibleStatuses.size < ALL_JOB_STATUSES.length) n++;
    if (jobFilters.myJobsOnly) n++;
    if (bothTypesEnabled && (!jobFilters.typeCommercial || !jobFilters.typeResidential)) n++;
    return n;
  }, [jobFilters, bothTypesEnabled]);

  // Provide sidebar content for desktop two-column layout
  useEffect(() => {
    if (!stats.total) { onSidebarContent?.(null); return; }
    onSidebarContent?.(
      <div className="grid grid-cols-1 gap-1.5">
        {COUNTER_STATUSES.map(s => {
          const count = stats[s];
          const isActive = jobFilters.visibleStatuses.size === 1 && jobFilters.visibleStatuses.has(s);
          const classes = JOB_STATUS_CLASSES[s];
          return (
            <button
              key={s}
              type="button"
              onClick={() => handleStatTap(s)}
              className={`rounded-lg px-3 py-2 text-left transition-all border
                ${isActive
                  ? `${classes} border-current shadow-brand-sm`
                  : 'bg-white border-stone hover:border-plum/30'
                }`}
            >
              <span className="font-mono text-sm font-bold">{count}</span>
              <span className="text-[9px] font-semibold uppercase tracking-wider ml-2">
                {JOB_STATUS_LABEL[s]}
              </span>
            </button>
          );
        })}
      </div>,
    );
  }, [onSidebarContent, stats, jobFilters.visibleStatuses, handleStatTap]);

  return (
    <div className={embedded ? '' : 'px-4 pt-4 pb-8 max-w-2xl mx-auto'}>
      {/* Header */}
      {!embedded && (
        <div className="mb-4">
          <h1 className="font-display text-h1 text-midnight">{isMover ? 'My Jobs' : 'Jobs'}</h1>
          <p className="text-[13px] text-slate mt-0.5">
            {isMover ? 'Assigned moves and job details' : 'All assigned moves and pending assignments'}
          </p>
        </div>
      )}




      {/* Search + Sort + Filter */}
      <div className="flex items-center gap-2 mb-4">
        <div className="flex-1 relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search jobs…"
            className="w-full h-10 pl-9 pr-8 rounded-xl border border-stone bg-white
                       text-[13px] text-midnight placeholder:text-slate/60
                       focus:outline-none focus:border-plum focus:ring-1 focus:ring-plum/30
                       transition-colors"
          />
          {search && (
            <button type="button" onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center
                         rounded-full text-slate hover:text-midnight hover:bg-mist transition-colors"
              aria-label="Clear search"><X size={13} /></button>
          )}
        </div>

        <button type="button" onClick={() => setShowSort(true)}
          className="shrink-0 h-10 w-10 flex items-center justify-center rounded-xl border border-stone
                     bg-white text-slate hover:border-plum/40 hover:text-plum active:bg-mist transition-colors"
          aria-label="Sort">
          <ArrowUpDown size={16} />
        </button>

        <button type="button" onClick={() => setShowFilter(true)}
          className={`shrink-0 h-10 flex items-center justify-center rounded-xl border
                     bg-white transition-colors relative w-10
                     ${hasActiveFilter ? 'border-plum text-plum' : 'border-stone text-slate hover:border-plum/40 hover:text-plum active:bg-mist'}`}
          aria-label="Filter">
          <Filter size={16} />
          {activeFilterCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-plum text-white text-[9px] font-bold flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </button>

      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="text-center py-12">
          <p className="text-small text-rose font-medium">{error}</p>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && filteredJobs.length === 0 && (
        <div className="text-center py-16">
          <Truck size={40} className="mx-auto text-slate/40 mb-3" />
          <p className="text-small text-slate font-medium">
            {debouncedSearch || hasActiveFilter || false ? 'No jobs match your filters' : 'No jobs assigned yet'}
          </p>
          <p className="text-[12px] text-slate/60 mt-1">
            {debouncedSearch || hasActiveFilter || false
              ? 'Try adjusting your search or filters'
              : isMover
                ? 'Jobs will appear here once you are assigned as Crew Lead'
                : 'Jobs will appear here once quotes are closed and a Crew Lead is assigned'}
          </p>
        </div>
      )}

      {/* Jobs list */}
      {!loading && !error && filteredJobs.length > 0 && (
        <ul className="flex flex-col gap-2.5">
          {filteredJobs.map(job => (
            <li key={job.id}>
              <MoveCard
                data={{ ...job, type: job.quoteType }}
                stage="job"
                statusLabel={JOB_STATUS_LABEL[job.jobStatus] ?? job.jobStatus}
                statusClasses={JOB_STATUS_CLASSES[job.jobStatus] ?? 'bg-stone text-slate'}
                onClick={() => navigate(`/moves/${job.id}`)}
                onAssignCrew={() => setCrewAssignJob(job)}
                onAssignTruck={() => setTruckAssignJob(job)}
                disabled={noSubscription}
              />
            </li>
          ))}
        </ul>
      )}

      {/* Infinite scroll sentinel */}
      {!loading && hasMore && <div ref={sentinelRef} className="h-1" />}

      {/* Loading more */}
      {loadingMore && (
        <div className="flex justify-center py-4">
          <LoadingSpinner size="sm" />
        </div>
      )}

      {/* Sort sheet */}
      {showSort && (
        <SortSheet current={currentSort} onSelect={handleSortSelect} onClose={() => setShowSort(false)} />
      )}

      {/* Filter sheet */}
      {showFilter && (
        <FilterSheet
          filters={jobFilters}
          onApply={setJobFilters}
          onClose={() => setShowFilter(false)}
          showTypeFilter={bothTypesEnabled}
        />
      )}

      {/* Crew assignment modal */}
      {crewAssignJob && orgId && (
        <CrewAssignmentModal
          orgId={orgId}
          jobId={crewAssignJob.id}
          moveDate={crewAssignJob.movingDate?.preferredDate}
          crewNeeded={crewAssignJob.logisticsSummary?.crewSize ?? 0}
          driverQuantity={crewAssignJob.logisticsSummary?.driverQuantity ?? (crewAssignJob.logisticsSummary?.truckQuantity ?? 0)}
          quoteType={crewAssignJob.quoteType ?? 'commercial'}
          currentCrew={crewAssignJob.assignedCrew ?? []}
          onSave={handleCrewSave}
          onClose={() => setCrewAssignJob(null)}
        />
      )}

      {/* Truck assignment modal */}
      {truckAssignJob && orgId && (
        <TruckAssignmentModal
          orgId={orgId}
          jobId={truckAssignJob.id}
          moveDate={truckAssignJob.movingDate?.preferredDate}
          trucksNeeded={truckAssignJob.logisticsSummary?.truckQuantity ?? 0}
          currentTrucks={truckAssignJob.assignedTrucks ?? []}
          onSave={handleTruckSave}
          onClose={() => setTruckAssignJob(null)}
        />
      )}

    </div>
  );
}
